import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import {
  ApiCategory,
  ApiCompetitionEntryInline,
  ApiRoute,
  ApiRouteAttempt,
} from "../../types";
import Avatar from "../Avatar";
import * as Style from "./RouteEntryItem.style";

type Props = {
  entry: Omit<ApiCompetitionEntryInline, "category"> & {
    category?: ApiCategory;
  };
  attempts?: ApiRouteAttempt[];
  children?: ReactNode;
  onClick?: () => void;
  climbType: "BOULDER" | "SPORT";
  showHandle?: boolean;
  route?: ApiRoute;
};

export const AttemptsPreview = ({
  attempts,
  segmentsCount,
  climbType,
}: {
  attempts: ApiRouteAttempt[];
  segmentsCount?: number;
  climbType: "BOULDER" | "SPORT";
}) => {
  if (climbType === "BOULDER") {
    return (
      <Style.AttemptsInline>
        {[...Array(5)].map((_, i) => {
          let content, isSummary;
          if (attempts.length > 5) {
            if (
              attempts[attempts.length - 1]?.segmentsCount ||
              attempts[attempts.length - 1]?.success
            ) {
              isSummary = i === 3;
            } else {
              isSummary = i === 4;
            }
          }
          let attempt;
          if (attempts.length > 5 && i === 4) {
            attempt = attempts[attempts.length - 1];
          } else {
            attempt = attempts[i];
          }
          if (isSummary) {
            content = `+${attempts.length - 4}`;
          } else if (attempt?.success) {
            content = "T";
          } else if (attempt?.segmentsCount) {
            if (segmentsCount === 1) {
              content = "Z";
            } else if (segmentsCount && segmentsCount > 1) {
              content = "Z" + attempt.segmentsCount;
            } else {
              content = "Z";
            }
          } else if (attempt) {
            content = "-";
          }

          return (
            <Style.AttemptsInlineAttempt
              key={i}
              success={!!attempt?.success}
              zone={!!attempt?.segmentsCount}
              isSummary={isSummary}
            >
              {content || ""}
            </Style.AttemptsInlineAttempt>
          );
        })}
      </Style.AttemptsInline>
    );
  } else {
    return (
      <strong>
        {attempts[0]?.success ? (
          "TOP"
        ) : (
          <>
            {attempts[0]?.segmentsCount}
            {attempts[0]?.usedLastHold ? "+" : ""}
          </>
        )}
      </strong>
    );
  }
};

export default function RouteEntryItem({
  route,
  entry,
  attempts,
  children,
  onClick,
  climbType,
  showHandle,
}: Props) {
  const navigate = useNavigate();
  return (
    <Style.Wrapper
      onClick={onClick ? onClick : () => navigate(entry.user.uuid)}
    >
      {showHandle ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="#00000055"
          viewBox="0 0 16 16"
          className="me-2"
        >
          <path
            fillRule="evenodd"
            d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
          />
        </svg>
      ) : null}
      <Style.Left>
        <Avatar user={entry.user} size={30} />
      </Style.Left>
      <Style.Right>
        <Style.Name>
          {entry.user.firstName} {entry.user.lastName}
        </Style.Name>
        <Style.Sub>{entry.category?.name}</Style.Sub>
      </Style.Right>
      <AttemptsPreview
        segmentsCount={route?.segmentsCount ?? undefined}
        attempts={attempts || []}
        climbType={climbType}
      />
      {children}
    </Style.Wrapper>
  );
}
